/* eslint-disable @typescript-eslint/naming-convention */
import { FC, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Button, Flex, Text, TextField } from '@servicetitan/anvil2';
import { AirflowDiagnosticRequestDto } from '../../models/calculator.dto';
import { FormState } from 'formstate';
import { InputFieldState } from '@servicetitan/form-state';
import { useDependencies } from '@servicetitan/react-ioc';
import { CalculatorStore } from '../../stores/calculator.store';
import { observer } from 'mobx-react';

export const AirflowDiagnosticsForm: FC<{
    className?: string;
    onSubmit?: (params: AirflowDiagnosticRequestDto) => void;
}> = observer(({ className }) => {
    const [store] = useDependencies<[CalculatorStore]>(CalculatorStore);
    const [isLoading, setIsLoading] = useState(false);

    const formState = useMemo(() => {
        return new FormState({
            return_air: new InputFieldState<string>('').validators(value => !value && 'Required'),
            supply_air: new InputFieldState<string>('').validators(value => !value && 'Required'),
            nominal_tons: new InputFieldState<string>('').validators(value => !value && 'Required'),
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        const validateResult = await formState.validate();
        if (validateResult.hasError) {
            return;
        }
        const params: AirflowDiagnosticRequestDto = {
            tool_name: 'airflow-diagnostic',
            tool_input: {
                return_air: Number(formState.$.return_air.$),
                supply_air: Number(formState.$.supply_air.$),
                nominal_tons: Number(formState.$.nominal_tons.$),
            },
        };
        setIsLoading(true);
        await store.submitCalculation(params);
        setIsLoading(false);
    }, [formState, store]);

    return (
        <Flex direction="column" gap="4" className={classNames('w-100-i', className)}>
            <Text size="small" subdued>
                Please provide the following info:
            </Text>
            <TextField
                required
                error={formState.$.return_air.error}
                label="Return Air (°F)"
                type="number"
                value={formState.$.return_air.value}
                onChange={e => formState.$.return_air.onChange(e.target.value)}
            />
            <TextField
                required
                error={formState.$.supply_air.error}
                label="Supply Air (°F)"
                type="number"
                value={formState.$.supply_air.value}
                onChange={e => formState.$.supply_air.onChange(e.target.value)}
            />
            <TextField
                required
                error={formState.$.nominal_tons.error}
                label="Nominal Tons"
                type="number"
                step={0.5}
                value={formState.$.nominal_tons.value}
                onChange={e => formState.$.nominal_tons.onChange(e.target.value)}
            />
            <Button
                loading={isLoading}
                alignSelf="self-end"
                appearance="primary"
                size="small"
                onClick={handleSubmit}
            >
                Submit
            </Button>
        </Flex>
    );
});
